<script setup lang="ts">
import {
  createBreadcrumb,
  deleteBreadcrumb,
  updateBreadcrumb,
  type Breadcrumb,
} from '@/api/breadcrumbs';
import InputText from './forms/InputText.vue';
import { useNestedMV } from '@/utilities/useInternalState';
import ButtonComponent from './ButtonComponent.vue';
import { getEmptyLocaleValue } from '@/api/types';
import InputBlockPicker from './forms/InputBlockPicker.vue';

const props = defineProps<{
  modelValue: Breadcrumb;
  edit?: boolean;
  selectedId?: number;
}>();

const emit = defineEmits<{
  (e: 'selected', value: Breadcrumb): void;
  (e: 'update:modelValue', value: typeof props.modelValue): void;
  (e: 'delete'): void;
}>();

const value = useNestedMV(props, (val) => emit('update:modelValue', val));

async function save() {
  if (!value.value.id) {
    const result = await createBreadcrumb(value.value);
    value.value.id = result.id;
  } else {
    await updateBreadcrumb(value.value);
  }
}

async function deleteChild() {
  if (value.value.id) {
    await deleteBreadcrumb(value.value.id);
    emit('delete');
  }
}

function addChild() {
  value.value.children ??= [];
  value.value.children.push({
    parent: { id: value.value.id, label: value.value.label },
    label: getEmptyLocaleValue(),
  });
}
</script>

<template>
  <div class="mb-2 flex items-center">
    <p
      v-if="!edit"
      class="mr-4 cursor-pointer hover:underline"
      :class="{ 'font-bold underline': value.id === selectedId }"
      @click="emit('selected', modelValue)"
    >
      {{ modelValue.label.no }}
    </p>

    <div v-else class="flex items-center space-x-2">
      <InputText
        v-model="value.label.no"
        label="categoryAdmin.breadcrumbName"
      />
      <InputBlockPicker
        v-model="value.homePageId"
        label="categoryAdmin.connectedHomePage"
      />
      <div class="mt-8">
        <ButtonComponent
          icon-class="text-2xl"
          accessible-button
          text="buttons.new_page"
          icon="add"
          @click="addChild"
        />
        <ButtonComponent
          icon-class="text-green-600 text-2xl"
          accessible-button
          text="buttons.save"
          icon="check"
          @click="save"
        />
        <ButtonComponent
          v-if="!value.children && value.parent"
          type="danger"
          accessible-button
          icon-class="text-2xl"
          text="buttons.delete"
          icon="delete"
          @click="deleteChild"
        />
      </div>
    </div>
  </div>

  <div class="ml-10">
    <BreadCrumbTree
      v-for="child in modelValue.children"
      :key="child.id"
      :model-value="child"
      :edit="edit"
      :selected-id="selectedId"
      @selected="(value) => emit('selected', value)"
      @delete="
        () =>
          (value.children = value.children?.filter((c) => c.id !== child.id))
      "
    />
  </div>
</template>
